<template>
  <div class="max-w-6xl mx-auto" :class="{ 'opacity-90': readOnlyConfig?.isReadOnly }">
    <!-- Question principale compacte -->
    <div class="text-center mb-4">
      <h1 class="text-lg font-semibold text-gray-900">
        {{ readOnlyConfig?.isReadOnly ? 'Type d\'événement :' : 'Type d\'événement ?' }}
      </h1>
    </div>

    <!-- Mode readonly : Affichage des informations sélectionnées -->
    <div v-if="readOnlyConfig?.isReadOnly && selectedType">
      <!-- Type sélectionné -->
      <div class="mb-6 text-center">
        <div class="inline-block p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
          <div class="font-semibold text-blue-900">{{ selectedType }}</div>
        </div>
      </div>

      <!-- Schéma sélectionné -->
      <div v-if="selectedSchemaData" class="mb-6">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">Schéma sélectionné</h2>
        <div class="p-5 bg-emerald-50 border-2 border-emerald-200 rounded-lg">
          <h3 class="text-base font-semibold text-emerald-900 mb-2">
            {{ selectedSchemaData.appellation }}
          </h3>
          <p class="text-sm text-emerald-700">
            {{ selectedSchemaData.description || 'Aucune description disponible' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Mode édition : Interface interactive -->
    <div v-if="!readOnlyConfig?.isReadOnly">
      <!-- Sélecteur de types -->
      <div class="mb-6">
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          <button v-for="type in uniqueSchemaTypes" :key="type"
            class="group relative p-4 bg-white border-2 border-gray-200 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg text-center"
            :class="{
              'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-400 shadow-lg transform scale-105 ring-2 ring-blue-200': selectedType === type,
              'hover:border-gray-300 hover:shadow-md': selectedType !== type
            }" @click="selectType(type === selectedType ? null : type)">
            <!-- Indicateur de sélection -->
            <div v-if="selectedType === type"
              class="absolute top-2 right-2 w-5 h-5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-md">
              <CheckIcon class="w-3 h-3 text-white" />
            </div>

            <!-- Contenu du type -->
            <div class="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-200">
              {{ type }}
            </div>
          </button>
        </div>
      </div>

      <!-- Champ de recherche compact -->
      <transition name="search-field" mode="out-in">
        <div v-if="selectedType" class="mb-5">
          <div class="relative">
            <input id="schema-search" v-model="searchQuery" type="text" name="schema-search"
              placeholder="Rechercher un schéma..."
              class="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-base font-medium placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 pl-12"
              @input="searchSchemas" />
            <!-- Icône de recherche -->
            <div class="absolute left-4 top-1/2 transform -translate-y-1/2">
              <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </transition>

      <!-- Liste des schémas -->
      <div v-if="selectedType">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">
          Schémas disponibles ({{ filteredSchemas.length }})
        </h2>

        <transition-group name="schema-list" tag="div" class="grid grid-cols-1 md:grid-cols-2 gap-4"
          :class="{ 'no-transition': previousType && selectedType }">
          <div v-for="(schema, index) in filteredSchemas" :key="schema.uuid"
            class="group relative p-5 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
            :class="{
              'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-400 shadow-xl transform scale-105 ring-2 ring-emerald-200': selectedSchema === schema.uuid,
              'hover:border-gray-300 hover:shadow-lg': selectedSchema !== schema.uuid
            }" :style="{
              transitionDelay: previousType ? '0ms' : `${index * 30}ms`
            }" @click="selectSchema(schema)">
            <!-- Badge de sélection -->
            <div v-if="selectedSchema === schema.uuid"
              class="absolute top-3 right-3 w-6 h-6 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-full flex items-center justify-center shadow-lg">
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clip-rule="evenodd" />
              </svg>
            </div>

            <!-- Contenu du schéma -->
            <div class="pr-8">
              <h3
                class="text-base font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors duration-200 mb-2">
                {{ schema.appellation }}
              </h3>
              <p class="text-sm text-gray-600 leading-relaxed">
                {{ schema.description || 'Aucune description disponible' }}
              </p>
            </div>
          </div>
        </transition-group>

        <!-- Message si aucun résultat -->
        <div v-if="filteredSchemas.length === 0" class="text-center py-4">
          <div class="w-8 h-8 mx-auto mb-2 bg-gray-100 rounded-full flex items-center justify-center">
            <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 class="text-sm font-medium text-gray-900 mb-1">Aucun résultat</h3>
          <p class="text-xs text-gray-600">Modifiez votre recherche.</p>
        </div>
      </div>

      <!-- Message d'instruction initial -->
      <div v-if="!selectedType" class="text-center py-4">
        <div
          class="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center">
          <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 mb-2">Sélectionnez un type</h3>
        <p class="text-xs text-gray-600">Choisissez pour voir les schémas.</p>
      </div>
    </div>

    <!-- Message si aucune sélection en readonly -->
    <div v-if="readOnlyConfig?.isReadOnly && !selectedType" class="text-center py-8">
      <div class="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
        <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      </div>
      <h3 class="text-base font-medium text-gray-700 mb-2">Aucun schéma sélectionné</h3>
      <p class="text-sm text-gray-500">Ce MEMoS n'a pas de schéma associé</p>
    </div>
  </div>
</template>

<script lang="ts">
import type { ReadOnlyConfig } from "@/types/readOnly";
import { CheckIcon } from 'lucide-vue-next';
import { computed, defineComponent, onMounted, ref } from 'vue';
import { schemas } from "../../api";

interface Schema {
  uuid: string;
  type: string;
  appellation: string;
  description?: string;
}

export default defineComponent({
  name: 'SchemaStep',
  components: {
    CheckIcon
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    readOnlyConfig: {
      type: Object as () => ReadOnlyConfig,
      required: false,
      default: () => ({ isReadOnly: false, allowNavigation: true, showEditHints: true })
    }
  },
  emits: ['update:model-value', 'is-valid', 'schema-selected', 'schema-type-selected'],
  setup(props, { emit }) {
    const allSchemas = ref<Schema[]>([]);
    const searchQuery = ref('');
    const selectedSchema = ref('');
    const selectedType = ref('');
    const previousType = ref('');
    const isSchemaSelected = ref(false);

    // Computed pour obtenir les données du schéma sélectionné
    const selectedSchemaData = computed(() => {
      if (!selectedSchema.value) return null;
      return allSchemas.value.find(schema => schema.uuid === selectedSchema.value);
    });

    // Charger tous les schémas
    onMounted(async () => {
      try {
        allSchemas.value = await schemas.fetchAllSchemas();

        // Restaurer l'état si un schéma était sélectionné
        if (props.modelValue.schema_model_uuid) {
          const selectedSchemaData = allSchemas.value.find(
            schema => schema.uuid === props.modelValue.schema_model_uuid
          );

          if (selectedSchemaData) {
            // Restaurer le schéma sélectionné
            selectedSchema.value = selectedSchemaData.uuid;
            // Restaurer le type sélectionné
            selectedType.value = selectedSchemaData.type;
            isSchemaSelected.value = true;
            emit('is-valid', true);
          }
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des schémas :", error);
        allSchemas.value = [];
      }
    });

    // Filtrer les schémas en fonction de la recherche et du type sélectionné
    const filteredSchemas = computed((): Schema[] => {
      let filtered = allSchemas.value;

      if (selectedType.value) {
        filtered = filtered.filter(schema => schema.type === selectedType.value);
      }

      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        filtered = filtered.filter(schema =>
          schema.appellation.toLowerCase().includes(query) ||
          (schema.description && schema.description.toLowerCase().includes(query))
        );
      }

      return filtered;
    });

    // Sélectionner un schéma
    const selectSchema = (schema: Schema): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      selectedSchema.value = schema.uuid;
      isSchemaSelected.value = true;

      // Mettre à jour le modèle et émettre les événements immédiatement
      const updatedModelValue = {
        ...props.modelValue,
        schema_model_uuid: schema.uuid,
        schema_model_type: schema.type,
        selectedSchema: schema
      };
      //console.log('SchemaStep: Schéma sélectionné:', schema.uuid, schema.type, schema);
      emit('update:model-value', updatedModelValue);

      // Émettre que l'étape est maintenant valide
      emit('is-valid', true);
      emit('schema-selected', schema);
      emit('schema-type-selected', schema.type);
    };

    // Sélectionner un type de schéma
    const selectType = (type: string | null): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      previousType.value = selectedType.value;
      selectedType.value = type || '';
      if (!type) {
        // Réinitialiser la sélection du schéma si on désélectionne le type
        selectedSchema.value = '';
        searchQuery.value = '';
        isSchemaSelected.value = false;
        emit('update:model-value', {
          ...props.modelValue,
          schema_model_uuid: null
        });
        emit('is-valid', false);
      }
    };

    // Rechercher des schémas
    const searchSchemas = (): void => {
      if (props.readOnlyConfig?.isReadOnly) return;
      // La recherche est gérée par le computed filteredSchemas
    };

    // Obtenir les types uniques de schémas
    const uniqueSchemaTypes = computed((): string[] => {
      const types = new Set(allSchemas.value.map(schema => schema.type));
      return Array.from(types);
    });

    return {
      searchQuery,
      filteredSchemas,
      selectedSchema,
      selectedSchemaData,
      selectSchema,
      searchSchemas,
      selectedType,
      selectType,
      uniqueSchemaTypes,
      previousType,
      isSchemaSelected
    };
  }
});
</script>

<style scoped>
/* Transition pour l'apparition du champ de recherche */
.search-field-enter-active,
.search-field-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.search-field-enter-from,
.search-field-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Transitions pour la liste des schémas */
.schema-list-enter-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.schema-list-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.schema-list-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.schema-list-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

.schema-list-move {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Désactiver transitions lors du changement de type */
.no-transition * {
  transition: none !important;
}

/* Utilitaires Tailwind personnalisés */
.hover\:scale-102:hover {
  transform: scale(1.02);
}
</style>